import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Truck, Lightbulb, Move } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminTrailers = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    size_m2: '',
    has_lighting: false,
    is_mobile: true,
    images: [],
    print_width_mm: '',
    print_height_mm: '',
    eyelet_count: '',
    eyelet_spacing_mm: '',
    dimension_notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTrailers();
  }, []);

  const fetchTrailers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/trailers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTrailers(data);
    } catch (error) {
      console.error('Error fetching trailers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (trailer = null) => {
    if (trailer) {
      setSelectedTrailer(trailer);
      setFormData({
        name: trailer.name,
        description: trailer.description || '',
        size_m2: trailer.size_m2.toString(),
        has_lighting: trailer.has_lighting,
        is_mobile: trailer.is_mobile,
        images: trailer.images || []
      });
    } else {
      setSelectedTrailer(null);
      setFormData({
        name: '',
        description: '',
        size_m2: '',
        has_lighting: false,
        is_mobile: true,
        images: []
      });
    }
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        size_m2: parseFloat(formData.size_m2),
        images: (formData.images || []).filter(url => url.trim() !== ''),
        print_width_mm: formData.print_width_mm ? parseInt(formData.print_width_mm) : 0,
        print_height_mm: formData.print_height_mm ? parseInt(formData.print_height_mm) : 0,
        eyelet_count: formData.eyelet_count ? parseInt(formData.eyelet_count) : 0,
        eyelet_spacing_mm: formData.eyelet_spacing_mm ? parseInt(formData.eyelet_spacing_mm) : 0,
        dimension_notes: formData.dimension_notes || ''
      };

      const url = selectedTrailer
        ? `${API_URL}/api/admin/trailers/${selectedTrailer.id}`
        : `${API_URL}/api/admin/trailers`;
      
      const method = selectedTrailer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchTrailers();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving trailer:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTrailer) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/trailers/${selectedTrailer.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchTrailers();
        setDeleteDialogOpen(false);
        setSelectedTrailer(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting trailer:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">{t('admin.trailers')}</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{trailers.length} Anhänger</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-trailer-button">
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.createTrailer')}
        </Button>
      </div>

      {/* Trailers Grid */}
      {trailers.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <Truck className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Anhänger</h3>
            <p className="text-[hsl(var(--muted-foreground))] mb-4">Erstellen Sie Ihren ersten Werbeanhänger.</p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Anhänger erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trailers.map((trailer, index) => (
            <motion.div
              key={trailer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="card-shadow overflow-hidden">
                {trailer.images && trailer.images.length > 0 && (
                  <div className="h-36 overflow-hidden">
                    <img src={trailer.images[0]} alt={trailer.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[hsl(var(--primary)/0.1)] rounded-xl flex items-center justify-center">
                      <Truck className="w-6 h-6 text-[hsl(var(--primary))]" />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(trailer)} data-testid={`edit-trailer-${trailer.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedTrailer(trailer); setDeleteDialogOpen(true); }} data-testid={`delete-trailer-${trailer.id}`}>
                        <Trash2 className="w-4 h-4 text-[hsl(var(--destructive))]" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold mb-2">{trailer.name}</h3>
                  {trailer.description && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{trailer.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{trailer.size_m2} m²</Badge>
                    {trailer.has_lighting && (
                      <Badge variant="secondary" className="flex items-center">
                        <Lightbulb className="w-3 h-3 mr-1" /> LED
                      </Badge>
                    )}
                    {trailer.is_mobile && (
                      <Badge variant="secondary" className="flex items-center">
                        <Move className="w-3 h-3 mr-1" /> Mobil
                      </Badge>
                    )}
                    {!trailer.is_active && (
                      <Badge variant="destructive">Inaktiv</Badge>
                    )}
                  </div>
                  {(trailer.print_width_mm > 0 || trailer.print_height_mm > 0) && (
                    <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] text-xs text-[hsl(var(--muted-foreground))]">
                      <span className="font-medium">Druckmasse:</span>{' '}
                      {trailer.print_width_mm}×{trailer.print_height_mm} mm
                      {trailer.eyelet_count > 0 && ` | ${trailer.eyelet_count} Ösen`}
                      {trailer.eyelet_spacing_mm > 0 && ` (${trailer.eyelet_spacing_mm}mm Abstand)`}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTrailer ? t('admin.editTrailer') : t('admin.createTrailer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('admin.trailerName')} *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="trailer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('admin.trailerDescription')}</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  data-testid="trailer-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="size_m2">{t('admin.trailerSize')} *</Label>
                <Input
                  id="size_m2"
                  name="size_m2"
                  type="number"
                  step="0.1"
                  value={formData.size_m2}
                  onChange={handleInputChange}
                  required
                  data-testid="trailer-size-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="has_lighting">{t('admin.trailerLighting')}</Label>
                <Switch
                  id="has_lighting"
                  checked={formData.has_lighting}
                  onCheckedChange={(checked) => handleSwitchChange('has_lighting', checked)}
                  data-testid="trailer-lighting-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="is_mobile">{t('admin.trailerMobile')}</Label>
                <Switch
                  id="is_mobile"
                  checked={formData.is_mobile}
                  onCheckedChange={(checked) => handleSwitchChange('is_mobile', checked)}
                  data-testid="trailer-mobile-switch"
                />
              </div>

              {/* Dimension Section Header */}
              <div className="pt-2 pb-1 border-t border-[hsl(var(--border))]">
                <Label className="text-sm font-semibold">Druckmasse & Ösen</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="print_width_mm" className="text-xs">Breite (mm)</Label>
                  <Input
                    id="print_width_mm"
                    name="print_width_mm"
                    type="number"
                    value={formData.print_width_mm}
                    onChange={handleInputChange}
                    placeholder="z.B. 3500"
                    data-testid="trailer-width-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="print_height_mm" className="text-xs">Höhe (mm)</Label>
                  <Input
                    id="print_height_mm"
                    name="print_height_mm"
                    type="number"
                    value={formData.print_height_mm}
                    onChange={handleInputChange}
                    placeholder="z.B. 2000"
                    data-testid="trailer-height-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="eyelet_count" className="text-xs">Anzahl Ösen</Label>
                  <Input
                    id="eyelet_count"
                    name="eyelet_count"
                    type="number"
                    value={formData.eyelet_count}
                    onChange={handleInputChange}
                    placeholder="z.B. 12"
                    data-testid="trailer-eyelet-count-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="eyelet_spacing_mm" className="text-xs">Ösenabstand (mm)</Label>
                  <Input
                    id="eyelet_spacing_mm"
                    name="eyelet_spacing_mm"
                    type="number"
                    value={formData.eyelet_spacing_mm}
                    onChange={handleInputChange}
                    placeholder="z.B. 300"
                    data-testid="trailer-eyelet-spacing-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="dimension_notes" className="text-xs">Bemerkungen zu Massen</Label>
                <Textarea
                  id="dimension_notes"
                  name="dimension_notes"
                  value={formData.dimension_notes}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="z.B. Ösen oben und unten, 5cm Rand lassen"
                  data-testid="trailer-dimension-notes-input"
                />
              </div>

              {/* Images Section */}
              <div className="pt-2 border-t border-[hsl(var(--border))]">
                <Label>Bilder (URLs)</Label>
              </div>
              <div className="space-y-2">
                <div className="space-y-2">
                  {(formData.images || []).map((url, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={url}
                        onChange={(e) => {
                          const newImages = [...(formData.images || [])];
                          newImages[idx] = e.target.value;
                          setFormData(prev => ({ ...prev, images: newImages }));
                        }}
                        placeholder="https://example.com/bild.jpg"
                        className="flex-1 text-sm"
                        data-testid={`trailer-image-url-${idx}`}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const newImages = (formData.images || []).filter((_, i) => i !== idx);
                        setFormData(prev => ({ ...prev, images: newImages }));
                      }}>×</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ''] }));
                  }} data-testid="trailer-add-image-button">
                    + Bild-URL hinzufügen
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={saving} data-testid="trailer-save-button">
                {saving ? 'Speichern...' : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anhänger löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Anhänger "{selectedTrailer?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[hsl(var(--destructive))]">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminTrailers;
